import React from "react";
import { useParams, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Settings, Flag, LogOut, Loader2, MapPin, Globe2, ArrowLeft, Search, ShieldAlert, Archive } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loading } from "@/components/common/Loading";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { ReportDialog } from "@/components/social/ReportDialog";
import { MODE_LABEL } from "@/components/groups/GroupCard";
import { GroupMemberDirectory } from "@/components/groups/GroupMemberDirectory";
import { GroupChatterPanel } from "@/components/groups/GroupChatterPanel";
import { GroupContentDirectory } from "@/components/groups/GroupContentDirectory";
import { GroupContextRail } from "@/components/groups/GroupContextRail";
import { GroupNotificationMenu } from "@/components/groups/GroupNotificationMenu";
import { GROUP_TABS_LIST, GROUP_TAB_TRIGGER, contentOffsetPx, useStickyNavOffset, scrollSectionIntoView } from "@/components/groups/groupSticky";
import { useAuth } from "@/context/AuthContext";
import { useReferences, labelFor } from "@/hooks/useReferences";

const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

/** Join / Request dialog: rules acceptance (§10) + membership questions (§9). */
function JoinDialog({ open, onOpenChange, group, questions, request, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);
  const [answers, setAnswers] = React.useState({});
  const setA = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/groups/${group.id}/join`, {
        rules_accepted: accepted,
        answers: (questions || []).map((q) => ({ question_id: q.id, value: answers[q.id] ?? null })),
      });
      toast.success(request ? "Request sent to the organizers." : `You've joined ${group.display_name}.`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't complete that.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="group-join-dialog">
        <DialogHeader>
          <DialogTitle>{request ? "Request to join" : "Join"} {group.display_name}</DialogTitle>
          <DialogDescription>
            {request ? "An organizer reviews every request." : "You'll get access right away."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {(questions || []).map((q) => (
            <div key={q.id} className="space-y-1.5">
              <Label>
                {q.label}{q.required ? <span className="text-destructive"> *</span> : null}
              </Label>
              {q.type === "long_text" ? (
                <Textarea rows={3} maxLength={1000} onChange={(e) => setA(q.id, e.target.value)}
                          data-testid={`group-answer-${q.id}`} />
              ) : q.type === "checkbox" ? (
                <div className="flex items-center gap-2">
                  <Checkbox onCheckedChange={(v) => setA(q.id, Boolean(v))}
                            data-testid={`group-answer-${q.id}`} />
                  <span className="text-sm text-muted-foreground">I agree</span>
                </div>
              ) : ["dropdown", "radio"].includes(q.type) ? (
                <Select onValueChange={(v) => setA(q.id, v)}>
                  <SelectTrigger data-testid={`group-answer-${q.id}`}>
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent>
                    {(q.choices || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : q.type === "multi_checkbox" ? (
                <div className="space-y-1.5" data-testid={`group-answer-${q.id}`}>
                  {(q.choices || []).map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm">
                      <Checkbox onCheckedChange={(v) => {
                        const cur = new Set(answers[q.id] || []);
                        if (v) cur.add(c); else cur.delete(c);
                        setA(q.id, Array.from(cur));
                      }} />{c}
                    </label>
                  ))}
                </div>
              ) : (
                <Input type={["number", "rating"].includes(q.type) ? "number" : q.type === "phone" ? "tel" : "text"}
                       maxLength={1000} onChange={(e) => setA(q.id, e.target.value)}
                       data-testid={`group-answer-${q.id}`} />
              )}
            </div>
          ))}
          {group.rules ? (
            <div className="space-y-2 rounded-lg border p-3">
              <p className="text-sm font-medium">Group rules</p>
              <RichTextView html={group.rules} className="text-muted-foreground" />
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(Boolean(v))}
                          data-testid="group-rules-accept" />
                I agree to the group rules
              </label>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="group-join-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {request ? "Send request" : "Join group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Phase 7 (Decision 5) — a Group the viewer can't open.
 *
 * For everyone this is the ordinary uniform "not found". For an active Owner/Admin of a
 * SUSPENDED Group it becomes a read-only suspension notice with a request-review action.
 * The server decides: the restricted endpoint is the only thing that answers while a hold
 * is in force, it never returns platform notes or the moderator's identity, and organizers
 * still cannot post, message, edit or manage anything. */
function GroupUnavailable({ handle, onBack }) {
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const susp = useQuery({
    queryKey: ["group-suspension", handle],
    queryFn: async () => (await api.get(`/groups/by-handle/${handle}/suspension`)).data,
    retry: false,
  });
  const info = susp.data;
  // Phase 10 (GRP-DEC-212/220) — an ARCHIVED or pending-deletion Group shows the approved
  // archived message at its own URL. Everyone sees the same words; only the Owner also sees
  // the deletion countdown and the Restore action.
  const life = useQuery({
    queryKey: ["group-lifecycle", handle],
    queryFn: async () => (await api.get(`/groups/by-handle/${handle}/lifecycle`)).data,
    retry: false,
  });
  const lifeInfo = life.data;

  if (lifeInfo?.state && !info?.suspended) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Card className="space-y-4 border-0 p-8" data-testid="group-archived-notice">
          <div className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-lg font-semibold">
              {lifeInfo.display_name || "This group"} is archived
            </h1>
          </div>
          <p className="text-sm text-muted-foreground" data-testid="group-archived-message">
            {lifeInfo.message}
          </p>
          {lifeInfo.pending_deletion ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
                 data-testid="group-pending-deletion-notice">
              <p className="font-medium text-destructive">Scheduled for permanent deletion</p>
              <p className="mt-1 text-muted-foreground">
                Everything in this group is removed permanently on{" "}
                {lifeInfo.purge_at ? new Date(lifeInfo.purge_at).toLocaleDateString() : "the scheduled date"}.
                Restore it before then to keep members, streams, posts and settings.
              </p>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {lifeInfo.can_restore ? (
              <Button size="sm" disabled={busy} data-testid="group-archived-restore"
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await api.post(`/groups/${lifeInfo.group_id}/restore`);
                          toast.success(lifeInfo.pending_deletion
                            ? "Deletion cancelled." : "Group restored.");
                          window.location.reload();
                        } catch (e) {
                          const d = e?.response?.data?.detail;
                          toast.error(typeof d === "string" ? d : "Couldn't restore that group.");
                          setBusy(false);
                        }
                      }}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {lifeInfo.pending_deletion ? "Cancel deletion" : "Restore group"}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={onBack}>Back to groups</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (info?.suspended) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Card className="space-y-4 border-0 p-8" data-testid="group-suspended-notice">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            <h1 className="text-lg font-semibold">
              {info.display_name || "This group"} is suspended
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Startup Otter has suspended this group while it is reviewed. Members and visitors
            can't open it, and posting, messaging and member management are unavailable.
          </p>
          {info.member_message ? (
            <p className="rounded-md bg-muted/60 p-3 text-sm" data-testid="group-suspended-message">
              {info.member_message}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {info.can_request_review && !sent ? (
              <Button size="sm" variant="outline" data-testid="group-suspension-review-request"
                      onClick={async () => {
                        try {
                          await api.post(`/groups/${info.group_id}/suspension/review-request`, { message: "" });
                          setSent(true);
                          toast.success("Review requested. Our team will take another look.");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Couldn't send that request.");
                        }
                      }}>
                Request a review
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground" data-testid="group-suspension-review-sent">
                {sent || info.review_requested_at ? "A review has been requested." : null}
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={onBack}>Back to groups</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Card className="border-0 p-10 text-center" data-testid="group-not-found">
        <p className="text-base font-semibold">Group not found</p>
        <p className="mt-1 text-sm text-muted-foreground">
          This group doesn't exist, or it isn't available to you.
        </p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          Back to groups
        </Button>
      </Card>
    </div>
  );
}


export default function GroupPage() {
  const { handle } = useParams();
  const { user } = useAuth();
  const { references } = useReferences();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const sectionRef = React.useRef(null);
  useStickyNavOffset();
  // Members land on the Group's conversation; a non-member still starts on Details
  // because Group Chatter is member-only (§13.3).
  const [tab, setTab] = React.useState(null);
  // Group Member Messaging V1 — the private conversation to open inside the Streams
  // surface, set by the Members-tab Message action or a `?thread=` deep link.
  const [openThreadId, setOpenThreadId] = React.useState(
    () => new URLSearchParams(window.location.search).get("thread") || null);
  // Group search (members only). `searchDraft` is what is typed; `searchQuery` is what was
  // SUBMITTED — the owner asked for Enter-to-search, not search-as-you-type, so one
  // keystroke never costs a query.
  const [searchDraft, setSearchDraft] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");
  // One measurement, one variable: whatever the Group chrome actually is (tab bar, plus
  // the member search bar, at whatever width), everything below offsets against it.
  const chromeRef = React.useRef(null);
  React.useEffect(() => {
    const el = chromeRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => document.documentElement.style.setProperty(
      "--so-tabbar-h", `${Math.round(el.offsetHeight)}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--so-tabbar-h");
    };
  });

  const [joining, setJoining] = React.useState(false);
  const [reporting, setReporting] = React.useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["group-page", handle],
    queryFn: async () => (await api.get(`/groups/by-handle/${handle}`)).data,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["group-page", handle] });
    qc.invalidateQueries({ queryKey: ["groups-mine"] });
  };

  // A notification deep link always targets the conversation, so `?post=` has to land the
  // reader on Streams whatever tab they were last on — including when this page is ALREADY
  // mounted and only the query changed. `location.key` is a dependency because React Router
  // gives every click its own key: that is what makes clicking the SAME notification twice,
  // or two notifications pointing at the same post, work instead of silently doing nothing.
  const [params] = useSearchParams();
  const navKey = useLocation().key;
  const deepLinkPost = params.get("post");
  React.useEffect(() => {
    if (deepLinkPost && data?.viewer?.is_member) setTab("chatter");
  }, [deepLinkPost, navKey, data?.viewer?.is_member]);

  // GRP-DEC-038: Group content is member-only, so no Group landing page is indexable in
  // Batch B (Listed-Group SEO is GRP-DEC-039, a later phase).
  React.useEffect(() => {
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex,nofollow");
    el.setAttribute("data-group-noindex", "1");
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  const leave = async () => {
    try {
      await api.post(`/groups/${data.group.id}/leave`);
      toast.success("You've left the group.");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't leave that group.");
    }
  };

  if (isLoading) return <div className="mx-auto max-w-7xl px-4 py-10"><Loading /></div>;
  if (isError || !data?.group) {
    return <GroupUnavailable handle={handle} onBack={() => navigate("/groups")} />;
  }

  const g = data.group;
  const v = data.viewer || {};
  // Group Member Messaging V1 — a Members-tab "Message" action, and a `?thread=` deep
  // link, both land on the conversation inside the Streams surface: the member stays in
  // the Group workspace rather than being sent to the platform-wide DM experience.
  const openThread = (threadId) => {
    setOpenThreadId(threadId);
    if (activeTab !== "chatter") pickTab("chatter");
  };
  const runSearch = (e) => {
    if (e) e.preventDefault();
    const q = searchDraft.trim();
    if (q.length < 2) return;
    setSearchQuery(q);
    // Results render where the Stream normally is, so the Streams surface is where the
    // reader has to be.
    if (activeTab !== "chatter") pickTab("chatter");
    // And they render at the TOP of that column, so hitting Enter from halfway down a
    // long feed pops up to the results rather than leaving them above the fold. Scrolls
    // up only — someone already at the top is left where they are.
    requestAnimationFrame(() => scrollSectionIntoView(sectionRef.current));
  };
  const clearSearch = () => { setSearchDraft(""); setSearchQuery(""); };
  const messageMember = async (person) => {
    try {
      const { data: created } = await api.post(`/groups/${g.id}/messages/threads`,
        { participant_ids: [person.user_id] });
      openThread(created.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open that conversation.");
    }
  };
  // Default tab: the conversation for a member, Details for everyone else.
  const activeTab = tab || (v.is_member ? "chatter" : "details");
  // Switching tab means starting a NEW surface, so land on the top of THAT section rather
  // than inheriting the previous tab's scroll position. The target is the tab bar's own
  // resting place, not the top of the page: the banner stays scrolled away and the section
  // begins immediately under the sticky bar. An anchor above the (sticky) bar is what gets
  // measured, since the bar itself reports its pinned position once you have scrolled past.
  const pickTab = (next) => {
    if (next === activeTab) return;
    setTab(next);
    // Land on the top of the SECTION the reader just picked — the top of the page would
    // put the banner back in the way. The target is the section's top tucked under the
    // sticky bar; it never changes with the section's content, because everything above it
    // is fixed height. A freshly mounted section is still fetching though, so for a frame
    // or two the document can be too short for the browser to honour the target — hence
    // the bounded nudge until it sticks.
    requestAnimationFrame(() => {
      const section = sectionRef.current;
      if (!section) return;
      // Offset against the WHOLE sticky Group chrome (`--so-tabbar-h` is the measured
      // height of the tab bar PLUS the member search bar), not the tab row alone —
      // measuring only the tabs lands the section's top underneath the search bar.
      const top = section.getBoundingClientRect().top + window.scrollY - contentOffsetPx();
      if (top <= 0) return;
      let tries = 0;
      const settle = () => {
        window.scrollTo({ top, behavior: "auto" });
        if (Math.abs(window.scrollY - top) > 1 && tries++ < 20) {
          requestAnimationFrame(settle);
        }
      };
      settle();
    });
  };
  const place = [g.location?.city, g.location?.state, g.location?.country].filter(Boolean).join(", ");

  // Group identity metadata, in the ONE approved order: Industry → Category →
  // Classification → Online → Location (GRP-DEC-307). Only values that exist are
  // pushed, so the separators can never be orphaned. Classification carries the
  // platform's teal accent badge — the treatment already used for a Capital type — so
  // it reads as a different classification dimension from Industry/Category
  // (GRP-DEC-308).
  const metaParts = [];
  const inds = g.industries || [];
  if (inds.length) {
    metaParts.push({ key: "industries", node: (
      <span className="capitalize text-foreground" data-testid="group-meta-industries">
        {inds.slice(0, 2).map((i) => labelFor(references.industries, i)).join(", ")}
        {inds.length > 2 ? ` +${inds.length - 2}` : ""}
      </span>) });
  }
  if (g.category) {
    metaParts.push({ key: "category", node: (
      <span className="capitalize text-foreground" data-testid="group-meta-category">
        {labelFor(references.group_categories, g.category)}
      </span>) });
  }
  (g.business_classifications || []).slice(0, 2).forEach((c) => {
    metaParts.push({ key: `class-${c}`, node: (
      <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50"
             data-testid={`group-meta-classification-${c}`}>
        {labelFor(references.business_classifications, c)}
      </Badge>) });
  });
  if (g.is_online) {
    metaParts.push({ key: "online", node: (
      <span className="inline-flex items-center gap-1" data-testid="group-meta-online">
        <Globe2 className="h-3.5 w-3.5" />Online
      </span>) });
  }
  if (g.is_local) {
    metaParts.push({ key: "local", node: (
      <Badge variant="outline" data-testid="group-local-badge">Local</Badge>) });
  }
  if (place) {
    metaParts.push({ key: "place", node: (
      <span className="inline-flex items-center gap-1" data-testid="group-meta-location">
        <MapPin className="h-3.5 w-3.5" />{place}
      </span>) });
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8" data-testid="group-page">
      {/* Same back-navigation treatment as an individual Event / Resource page. */}
      <Link to="/groups" data-testid="group-back-to-all"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to All Groups
      </Link>
      <div>
        {/* Banner — the canonical Business / Capital cover: borderless, full profile
            width, 4:1 asset at h-40 / sm:h-56 (`OrgProfileView`). */}
        <div className="relative h-40 w-full overflow-hidden bg-gradient-to-r from-teal-100 to-teal-50 shadow-[0_8px_30px_-12px_rgba(16,24,40,0.25)] sm:h-56">
          {g.cover_url ? (
            <img src={fileUrl(g.cover_url)} alt="" className="h-full w-full object-cover" />
          ) : null}
        </div>

        {/* Identity / details row — the canonical org identity Card. The thumbnail sits
            BESIDE the name and metadata and never overlaps the banner (GRP-DEC-304).
            `mt-3`: owner-directed, half the previous gap to the cover (GRP-DEC-325). */}
        <Card className="relative mt-3 border-0 p-6" data-testid="group-identity-card">
          {/* Identity on the left, the action cluster top-right on tablet/desktop
              (GRP-DEC-306). On phones the wrapper is a column, so the actions reflow
              BENEATH the identity block and wrap instead of overflowing. */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1 grid grid-cols-[5rem_minmax(0,1fr)] gap-x-4 gap-y-3 sm:grid-cols-[150px_minmax(0,1fr)]">
            <div className="h-20 w-20 shrink-0 overflow-hidden border-4 border-card bg-secondary shadow-sm sm:row-span-2 sm:h-[150px] sm:w-[150px]"
                 data-testid="group-thumbnail">
              {g.profile_image_url ? (
                <img src={fileUrl(g.profile_image_url)} alt={g.display_name}
                     className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-4xl text-muted-foreground">
                  {initials(g.display_name)}
                </div>
              )}
            </div>
            <div className="min-w-0 pt-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-[700] text-foreground sm:text-2xl" data-testid="group-name">
                  {g.display_name}
                </h1>
                {data.is_draft ? <Badge variant="secondary">Draft</Badge> : null}
                {!g.listed ? <Badge variant="outline">Unlisted</Badge> : null}
              </div>
              <p className="text-sm font-[500] text-primary">@{g.handle}</p>
            </div>
            <div className="col-span-2 min-w-0 sm:col-span-1 sm:col-start-2 sm:-mt-1"
                 data-testid="group-meta">
              {g.short_description ? (
                <p className="text-foreground">{g.short_description}</p>
              ) : null}
              {/* ONE metadata line on desktop, in the fixed order Industry → Category →
                  Classification → Online → Location (GRP-DEC-307). Empty values are
                  skipped, so no separator is ever orphaned; on narrow screens the row
                  wraps naturally in the same order rather than overflowing. */}
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-muted-foreground"
                   data-testid="group-metadata-line">
                {metaParts.map((part, idx) => (
                  <React.Fragment key={part.key}>
                    {idx > 0 ? <span aria-hidden="true" className="text-muted-foreground/50">·</span> : null}
                    {part.node}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1" data-testid="group-member-count">
                  <Users className="h-4 w-4" />{g.member_count} {g.member_count === 1 ? "member" : "members"}
                </span>
                <span>{MODE_LABEL[g.membership_mode]}</span>
              </div>
            </div>
          </div>
              <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end"
                   data-testid="group-actions">
            {v.can_join && user ? (
              <Button onClick={() => setJoining(true)} data-testid="group-join-button">Join group</Button>
            ) : null}
            {v.can_request && user ? (
              <Button onClick={() => setJoining(true)} data-testid="group-request-button">
                Request to join
              </Button>
            ) : null}
            {/* GRP-DEC-290: a logged-out visitor enters the EXISTING sign-in / create-account
                flow and comes back to this Group. Authentication makes them eligible to act;
                it never bypasses the Group's approval or invitation policy. */}
            {!user && (v.can_join || v.can_request) ? (
              <Button asChild data-testid="group-join-signin">
                <Link to={`/login?next=/g/${handle}`}>
                  {v.can_join ? "Join Startup Otter to join this group" : "Sign in to request to join"}
                </Link>
              </Button>
            ) : null}
            {v.request_pending ? (
              <Badge variant="secondary" data-testid="group-request-pending">Request pending</Badge>
            ) : null}
            {v.is_banned ? (
              <Badge variant="destructive" data-testid="group-banned-badge">You can't join this group</Badge>
            ) : null}
            {v.request_blocked && !v.is_member ? (
              <Badge variant="outline" data-testid="group-request-blocked">Request limit reached</Badge>
            ) : null}
            {v.is_admin ? (
              <Button asChild variant="outline" data-testid="group-manage-button">
                <Link to={`/dashboard/groups/${g.id}/manage`}>
                  <Settings className="mr-2 h-4 w-4" />Manage
                </Link>
              </Button>
            ) : null}
            {v.is_member && !v.is_owner ? (
              <Button variant="ghost" onClick={leave} data-testid="group-leave-button">
                <LogOut className="mr-2 h-4 w-4" />Leave
              </Button>
            ) : null}
            {v.is_member ? (
              <GroupNotificationMenu groupId={g.id} />
            ) : null}
            {user ? (
              <Button variant="ghost" size="icon" onClick={() => setReporting(true)}
                      title="Report group" data-testid="group-report-button">
                <Flag className="h-4 w-4" />
              </Button>
            ) : null}
              </div>
          </div>
        </Card>
      </div>

      {/* The Group's in-page chrome — tab bar AND (for members) the search bar — is ONE
          sticky block whose measured height is published as `--so-tabbar-h`. That is the
          variable every other Group surface already offsets against (the Stream rail, the
          pinned strip, post `scroll-margin-top`, the reveal-on-select scroll), so adding
          the search bar shifts all of them correctly instead of letting the rail slide
          underneath it. */}
      <div ref={chromeRef}
           className="sticky top-[var(--so-nav-offset)] z-20 -mx-1 mt-2 bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <Tabs value={activeTab} onValueChange={pickTab}
            className="pt-2">
        {/* The tab bar stays reachable through a long Stream or directory: it sticks
            directly under the app chrome using the ONE shared offset, so it never
            covers the banner and never shifts the layout (correction §1). The sticky
            element has to be the Tabs root — a sticky child of a box only as tall as
            itself has nowhere to stick.
            Phase 6 added two triggers; five of them overflow a 390px viewport, so the
            row scrolls inside its own box instead of widening the page. */}
        <div className="min-w-0 max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <TabsList className={GROUP_TABS_LIST} data-testid="group-tabs">
          {/* GRP-DEC-319: inside a Group the Chatter surface is called **Streams** (the
              global Chatter product is unchanged, and so is the engine underneath).
              GRP-DEC-305: the tab is "Details", not "About". */}
          {v.is_member ? (
            <TabsTrigger value="chatter" className={GROUP_TAB_TRIGGER} data-testid="group-tab-chatter">Streams</TabsTrigger>
          ) : null}
          <TabsTrigger value="details" className={GROUP_TAB_TRIGGER} data-testid="group-tab-details">Details</TabsTrigger>
          {v.is_member ? (
            <TabsTrigger value="members" className={GROUP_TAB_TRIGGER} data-testid="group-tab-members">Members</TabsTrigger>
          ) : null}
          {v.is_member ? (
            <TabsTrigger value="events" className={GROUP_TAB_TRIGGER} data-testid="group-tab-events">Events</TabsTrigger>
          ) : null}
          {v.is_member ? (
            <TabsTrigger value="resources" className={GROUP_TAB_TRIGGER} data-testid="group-tab-resources">Resources</TabsTrigger>
          ) : null}
        </TabsList>
        </div>
      </Tabs>

      {/* Group search — members only, and deliberately FULL WIDTH directly under the tab
          bar so it reads as "search this group", not "search this feed". It sticks below
          the tab bar using the same shared offsets every other Group surface uses, so it
          stays reachable however far down a Stream the reader is. */}
      {v.is_member ? (
        <form onSubmit={runSearch} className="pb-2 pt-2"
              data-testid="group-search-bar">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)}
                   placeholder="Search this group — people, posts, announcements, your messages, streams, events…"
                   aria-label="Search this group"
                   className="h-10 pl-9 pr-24"
                   data-testid="group-search-input" />
            <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
              {searchQuery ? (
                <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs"
                        onClick={clearSearch} data-testid="group-search-reset">Clear</Button>
              ) : null}
              <Button type="submit" size="sm" className="h-7 px-3 text-xs"
                      disabled={searchDraft.trim().length < 2}
                      data-testid="group-search-submit">Search</Button>
            </div>
          </div>
        </form>
      ) : null}
      </div>

      {/* The section wrapper is what "the top of this tab" means. It is measured rather
          than the (sticky) bar above it, whose own offsets report where it is currently
          pinned, not where it rests. The min-height guarantees the document is tall enough
          for the bar to actually PIN on a short tab like Details or Members — without it
          the browser clamps the scroll and the banner stays in view. */}
      <div ref={sectionRef}
           className="grid items-start gap-4 min-h-[calc(100dvh-var(--so-nav-offset)-var(--so-tabbar-h))] lg:grid-cols-[minmax(0,1fr)_288px]">
      {/* GRP-DEC-335 — the tab content and the ONE persistent Group context rail. At `lg`
          the rail is the right-hand column; below it the grid collapses to a single column
          and the rail reflows UNDER the content in the same order, rather than being
          squeezed beside the feed on a phone. */}
      <div className="min-w-0 space-y-6">
      {activeTab === "chatter" && v.is_member ? (
        <GroupChatterPanel group={g} viewer={v} profileId={v.personal_profile_id}
                           initialThreadId={openThreadId}
                           onThreadOpened={setOpenThreadId}
                           searchQuery={searchQuery} onClearSearch={clearSearch} />
      ) : null}

      {activeTab === "events" && v.is_member ? (
        <GroupContentDirectory group={g} kind="event" />
      ) : null}

      {activeTab === "resources" && v.is_member ? (
        <GroupContentDirectory group={g} kind="resource" />
      ) : null}

      {activeTab === "details" ? (
        <div data-testid="group-details-panel">
          <Card className="space-y-3 border-0 p-6">
            <h2 className="text-base font-semibold">About</h2>
            {/* Canonical rich-text renderer (client DOMPurify on top of the server-side
                allow-list sanitize) — the same component a Business About uses. */}
            {g.about ? (
              <div data-testid="group-about-body"><RichTextView html={g.about} /></div>
            ) : (
              <p className="text-sm text-foreground/80">
                {g.short_description || "The organizers haven't added a description yet."}
              </p>
            )}
            {/* Industry / Category / Classification are summarised on the identity
                metadata line (GRP-DEC-307), capped at two industries. The COMPLETE
                industry selection lives here as canonical chips (GRP-DEC-327) — labels
                come from the shared reference bundle, never the raw slug. */}
            {(g.industries || []).length ? (
              <div className="pt-2" data-testid="group-details-industries">
                <h3 className="text-sm font-semibold">Industry</h3>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {(g.industries || []).map((i) => (
                    <Badge key={`i-${i}`} variant="secondary" className="capitalize">
                      {labelFor(references.industries, i)}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
            {(g.topics || []).length ? (
              <div className="pt-2" data-testid="group-details-topics">
                <h3 className="text-sm font-semibold">Topics &amp; interests</h3>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {(g.topics || []).map((t) => (
                    <Badge key={`t-${t}`} variant="outline" className="capitalize">
                      {String(t).replace(/-/g, " ")}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}
            {/* Organizers, Affiliated Organizations and Group Rules all live in the ONE
                persistent context rail now (GRP-DEC-335) — they are not duplicated here. */}
            {v.is_member && g.welcome_message ? (
              <div className="rounded-lg bg-teal-50/50 p-4" data-testid="group-welcome-message">
                <RichTextView html={g.welcome_message} />
              </div>
            ) : null}
          </Card>
        </div>
      ) : null}

      {activeTab === "members" && v.is_member ? (
        <GroupMemberDirectory groupId={g.id} memberCount={g.member_count}
                              onMessage={g.allow_member_messages ? messageMember : null} />
      ) : null}
      </div>

      <GroupContextRail group={g} organizers={data.organizers || []}
                        affiliations={data.affiliations || []} />
      </div>

      <JoinDialog open={joining} onOpenChange={setJoining} group={g}
                  questions={data.questions || []} request={Boolean(v.can_request)}
                  onDone={refresh} />
      <ReportDialog open={reporting} onOpenChange={setReporting} contentType="group"
                    contentId={g.id} />
    </div>
  );
}
